import { Box, Card, CardContent, CardHeader, Collapse, Paper, Typography } from "@mui/material";
import BoyIcon from '@mui/icons-material/Boy';
import { OrdinaryButton } from "../../../../../common/Buttons";
import { withMultipleQueryData } from "../../../../../common/withQueryData";
import { usePropertyId } from "../../../../../common/hooks/usePropertyId";
import { useRoomsData } from "../../../../../common/hooks/useRoomsData";
import { usePlansData } from "../../../../../common/hooks/useRatePlansData";
import { useSearchData } from "../../../../../common/hooks/useSearchData";

const RoomSelector = (props) => {
    const { data, handleInputChange, handleNext } = props;
    const { checkin, checkout, adults } = data;

    const propertyId = usePropertyId();

    const { queryResults: roomsQueryResults } = useRoomsData(propertyId);
    const { queryResults: plansQueryResults } = usePlansData(propertyId);
    const { queryResults: searchQueryResults } = useSearchData(propertyId, checkin, checkout, adults);

    const searchQuery = {
        checkin,
        checkout,
        adults,
    }

    const onSelect = (roomId, planId) => {
        const payload = [
            {
                id: 'roomId',
                value: roomId,
            },
            {
                id: 'planId',
                value: planId,
            }
        ]

        handleInputChange(payload);

        handleNext();
    }

    return <RoomSelectorContentWithMultipleQueryData
        queryResults={{
            rooms: roomsQueryResults,
            plans: plansQueryResults,
            searchResults: searchQueryResults,
        }}
        searchQuery={searchQuery}
        onSelect={onSelect}
    />
}

const RoomSelectorContent = ({ queryResults, reloadAll, ...props }) => {
    const { rooms: roomsQueryResult, plans: plansQueryResult, searchResults: searchResultsQueryResult } = queryResults;

    /* eslint-disable no-unused-vars */
    const { data: rooms, refetch: reloadRooms } = roomsQueryResult;
    const { data: plans, refetch: reloadPlans } = plansQueryResult;
    const { data: searchResults, refetch: reloadSearchResults } = searchResultsQueryResult;
    /* eslint-enable no-unused-vars */

    return <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <RoomOffers roomOffers={searchResults} rooms={rooms} ratePlans={plans} {...props} />
    </Box>
}

const RoomSelectorContentWithMultipleQueryData = withMultipleQueryData(RoomSelectorContent)

export default RoomSelector;

const RoomOffers = (props) => {
    const { roomOffers, rooms, ratePlans, searchQuery, onSelect } = props;

    if (!roomOffers || roomOffers.length === 0) {
        return (
            <Typography sx={{ mt: 2, color: 'text.secondary' }}>
                No rooms available for the selected dates and occupancy.
            </Typography>
        );
    }

    return roomOffers.map((roomOffer) => <RoomOffer
        key={`roomOffer-${roomOffer.roomId}`}
        roomOffer={roomOffer}
        room={rooms.filter(room => room.id === roomOffer.roomId)[0]}
        ratePlans={ratePlans}
        searchQuery={searchQuery}
        onSelect={(planId) => onSelect(roomOffer.roomId, planId)}
    />)
}

const RoomOffer = (props) => {
    const { roomOffer, room, ratePlans, searchQuery, onSelect } = props;

    let occupancy = [];

    for (let i = 0; i < room.maxOccupancy; i++) {
        const translateX = Math.min((i * -10) - 7);
        occupancy.push(<BoyIcon key={i} sx={{ color: (i < searchQuery.adults ? 'primary' : 'primary.light'), transform: `translate(${translateX}px, 0px)` }} />)
    }

    return <Collapse in orientation="vertical" collapsedSize={0}>
        <Card sx={{ margin: '10px 0px', width: 'auto !important' }}>
            <CardHeader
                title={<><Typography sx={{ fontWeight: '700', maxWidth: '400px' }}>{room.name}</Typography>{occupancy}</>}
                sx={{ paddingBottom: '0px' }}
            />
            <CardContent sx={{ paddingTop: '0px' }}>
                {roomOffer.plans.map(planOffer => <PlanOffer planOffer={planOffer} ratePlans={ratePlans} searchQuery={searchQuery} onSelect={() => onSelect(planOffer.planId)} key={`roomOffer-${roomOffer.roomId}-plan-${planOffer.planId}`} />)}
            </CardContent>
        </Card>
    </Collapse>
}

const PlanOffer = (props) => {
    const { planOffer, ratePlans, searchQuery, onSelect } = props;

    const plan = ratePlans.filter(plan => plan.id === planOffer.planId)[0];

    const lengthOfStay = Math.ceil((searchQuery.checkout - searchQuery.checkin) / (60 * 60 * 24 * 1000));;

    const pricePerNight = planOffer.totalPrice / lengthOfStay;

    return <Paper sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', margin: '10px 0px 0px', padding: '10px', width: 'auto !important' }} key={planOffer.planId} variant='outlined' square>
        <Box sx={{ width: '250px' }}>
            <Box>
                <Typography sx={{ fontWeight: '700' }}>{plan.name}</Typography>
            </Box>
            <Box sx={{ fontSize: '0.9rem', fontWeight: '500', padding: '5px 0px' }}>
                <li>{plan.refundable ? 'Refundable' : 'Non refundable'}</li>
            </Box>
        </Box>
        <Box sx={{ width: '150px' }}>
            <Typography sx={{ fontWeight: '700' }}>PLN {planOffer.totalPrice}</Typography>
            <Typography sx={{ fontWeight: '400', fontSize: '0.8rem' }}>{`PLN ${pricePerNight} per night`}</Typography>
        </Box>
        <Box sx={{ width: '150px' }}>
            <OrdinaryButton text='Select' onClick={() => onSelect()} />
        </Box>
    </Paper>
}
